/**
 * Brand registry — the single source of truth for company-branded deck
 * styling. Each entry describes how a brand is gated (FactSet CompanyId,
 * with `id` doubling as the variation value served by the single
 * `pptxTemplateBrand` ConfigCat text setting — see `featureFlagService.ts`),
 * what it looks like (theme colors, fonts, chart palette, slide dimensions),
 * the prompt text appended when it is active, and the template assets
 * opened/loaded during deck creation.
 *
 * Adding a new company toggle should only require appending a new
 * `BrandConfig` entry here (plus a new variation on the shared
 * `pptxTemplateBrand` ConfigCat flag and template assets) — the gating, UI,
 * and generation pipeline all read from this registry.
 */

import type { SnippetType, ThemeColors } from "../../types";
import {
  CITI_EXTRA_SNIPPET_TYPES,
  CITI_SNIPPET_STYLE_PROFILE,
  DEFAULT_SNIPPET_STYLE_PROFILE,
  UBS_SNIPPET_STYLE_PROFILE,
  type SnippetStyleProfile,
} from "./snippetStyle";
import {
  CITI_THEME_COLORS,
  CITI_SLIDE_DIMENSIONS,
  CITI_CHART_SERIES_PALETTE,
  CITI_STYLE_PROMPT,
} from "./citiStyleConfig";
import {
  BMO_THEME_COLORS,
  BMO_SLIDE_DIMENSIONS,
  BMO_CHART_SERIES_PALETTE,
  BMO_STYLE_PROMPT,
} from "./bmoStyleConfig";
import {
  UBS_THEME_COLORS,
  UBS_SLIDE_DIMENSIONS,
  UBS_CHART_SERIES_PALETTE,
  UBS_STYLE_PROMPT,
} from "./ubsStyleConfig";
import {
  SAFRA_THEME_COLORS,
  SAFRA_SLIDE_DIMENSIONS,
  SAFRA_CHART_SERIES_PALETTE,
  SAFRA_STYLE_PROMPT,
} from "./safraStyleConfig";
import {
  EVERCORE_THEME_COLORS,
  EVERCORE_SLIDE_DIMENSIONS,
  EVERCORE_CHART_SERIES_PALETTE,
  EVERCORE_STYLE_PROMPT,
} from "./evercoreStyleConfig";
import {
  MESSIER_THEME_COLORS,
  MESSIER_SLIDE_DIMENSIONS,
  MESSIER_CHART_SERIES_PALETTE,
  MESSIER_STYLE_PROMPT,
} from "./messierStyleConfig";
import {
  CAVENDISH_THEME_COLORS,
  CAVENDISH_SLIDE_DIMENSIONS,
  CAVENDISH_CHART_SERIES_PALETTE,
  CAVENDISH_STYLE_PROMPT,
} from "./cavendishStyleConfig";
import {
  MA_FINANCIAL_THEME_COLORS,
  MA_FINANCIAL_SLIDE_DIMENSIONS,
  MA_FINANCIAL_CHART_SERIES_PALETTE,
  MA_FINANCIAL_STYLE_PROMPT,
} from "./maFinancialStyleConfig";
import {
  STJ_THEME_COLORS,
  STJ_SLIDE_DIMENSIONS,
  STJ_CHART_SERIES_PALETTE,
  STJ_STYLE_PROMPT,
} from "./stjStyleConfig";
import {
  BERENBERG_THEME_COLORS,
  BERENBERG_SLIDE_DIMENSIONS,
  BERENBERG_CHART_SERIES_PALETTE,
  BERENBERG_STYLE_PROMPT,
} from "./berenbergStyleConfig";
import {
  FLAGSTAFF_THEME_COLORS,
  FLAGSTAFF_SLIDE_DIMENSIONS,
  FLAGSTAFF_CHART_SERIES_PALETTE,
  FLAGSTAFF_STYLE_PROMPT,
} from "./flagstaffStyleConfig";
import {
  PARETO_THEME_COLORS,
  PARETO_SLIDE_DIMENSIONS,
  PARETO_CHART_SERIES_PALETTE,
  PARETO_STYLE_PROMPT,
} from "./paretoStyleConfig";
import {
  MEDIOBANCA_THEME_COLORS,
  MEDIOBANCA_SLIDE_DIMENSIONS,
  MEDIOBANCA_CHART_SERIES_PALETTE,
  MEDIOBANCA_STYLE_PROMPT,
} from "./mediobancaStyleConfig";
import {
  NORDEA_THEME_COLORS,
  NORDEA_SLIDE_DIMENSIONS,
  NORDEA_CHART_SERIES_PALETTE,
  NORDEA_STYLE_PROMPT,
} from "./nordeaStyleConfig";
import {
  FINSTER_DEMO_THEME_COLORS,
  FINSTER_DEMO_SLIDE_DIMENSIONS,
  FINSTER_DEMO_CHART_SERIES_PALETTE,
  FINSTER_DEMO_STYLE_PROMPT,
} from "./finsterDemoStyleConfig";

export type BrandId =
  | "citi"
  | "bmo"
  | "ubs"
  | "safra"
  | "evercore"
  | "messier"
  | "cavendish"
  | "maFinancial"
  | "stj"
  | "berenberg"
  | "flagstaff"
  | "pareto"
  | "mediobanca"
  | "nordea"
  | "default";

export interface BrandConfig {
  /**
   * Stable identifier threaded through the generation pipeline. Also the
   * variation value this brand is served as by the single `pptxTemplateBrand`
   * ConfigCat text setting (see `featureFlagService.ts`).
   */
  id: BrandId;
  /** Human-readable company name. */
  label: string;
  /** Compact label for the chat toggle (e.g. "Citi defaults"). */
  toggleLabel: string;
  /** Title shown in the deck-creator brand section. */
  sectionTitle: string;
  /** One-line description shown under the deck-creator brand section. */
  sectionDescription: string;
  /** Signature brand color (with leading '#') used for toggle chrome. */
  accentColorHex: string;
  /** FactSet CompanyId (auth payload `profile.customAttributes.CompanyId`). */
  companyId: string;
  /** Theme override applied to generated slides when active. */
  themeColors: ThemeColors;
  /** Native slide dimensions of the brand template (points). */
  slideDimensions: { width: number; height: number };
  /** Ordered chart series palette (bare hex, no '#'). */
  chartSeriesPalette: string[];
  /** Prompt text appended to the system prompt when the brand is active. */
  stylePrompt: string;
  /** Empty master deck (layouts only) opened as the new deck base. */
  blankTemplateAsset?: string;
  /** Full sample deck loaded for reference shapes during generation. */
  referenceTemplateAsset?: string;
  /**
   * Slide-layout name (by master layout name) that newly allocated content
   * slides should use, so they inherit brand furniture (logo, footer) when the
   * brand keeps that branding on its layouts rather than the slide master.
   * Leave undefined for brands whose logo lives on the slide master (Citi/BMO),
   * since default AddSlide already inherits it.
   */
  contentLayoutName?: string;
  /**
   * Whether snippets are on by default for this brand. Overridable via
   * localStorage `finster_enable_snippet_guidance`. Flip to true only after
   * `snippetStyleProfile` is filled in.
   */
  snippetsEnabled: boolean;
  /** Extra apply_edits snippet types for this brand only (not a global enum). */
  extraSnippetTypes?: readonly SnippetType[];
  /**
   * Visual contract for generic snippet layouts. Omitted brands use
   * DEFAULT_SNIPPET_STYLE_PROFILE (compact white tiles, thin accent strips).
   */
  snippetStyleProfile?: SnippetStyleProfile;
  /**
   * How `referenceTemplateAsset` is presented to the model.
   * "shapes-and-images" (default) serializes every reference slide's shapes and
   * renders the first N. "curated-images" attaches only the `referenceSlides`
   * pages as anchored screenshots, with no shape JSON, because the numeric
   * geometry lives in the brand style prompt instead.
   */
  referenceMode?: "shapes-and-images" | "curated-images";
  /** Curated reference pages for "curated-images" mode. `slideNumber` is 1-based. */
  referenceSlides?: Array<{ slideNumber: number; anchor: string; caption: string }>;
}

/**
 * The UBS sample pages worth showing the model. Colour swatch guides, blank
 * pages, and the 6.5pt data book teach nothing at screenshot resolution, so
 * they are left out; their rules are stated numerically in `UBS_STYLE_PROMPT`.
 *
 * `slideNumber` is 1-based into `UBS.pptx`. The cover, blank page, and colour
 * guide slides were dropped from the front of that deck (and the trailing
 * contact page from the end), so every number here is 3 less than in the
 * prior revision of the sample deck.
 */
const UBS_REFERENCE_SLIDES: NonNullable<BrandConfig["referenceSlides"]> = [
  {
    slideNumber: 11,
    anchor: "UBS-REF-PROFILE",
    caption:
      "Company profile: KPI tile row with accent strips, two-column split, ownership/acquisition timelines, leadership tiles, logo bands, product grid.",
  },
  {
    slideNumber: 5,
    anchor: "UBS-REF-FINTABLE",
    caption:
      "Canonical UBS financial table: unfilled cells, no vertical borders, 9pt headings over 10pt body, UBS row in red, subtotal lighter than total.",
  },
  {
    slideNumber: 4,
    anchor: "UBS-REF-CHARTPAGE",
    caption:
      "Chart page: three matched mix charts sharing one category-to-colour map, a stacked column, and a companion table at chart font size.",
  },
  {
    slideNumber: 8,
    anchor: "UBS-REF-TEXTTABLE",
    caption:
      "Text/assessment table: colour-strip key, Gray I and Pastel I text regions, 3pt white separators, Harvey-ball assessment column.",
  },
  {
    slideNumber: 12,
    anchor: "UBS-REF-CARDS",
    caption:
      "Transaction summary cards: four-across grid of two-row cards with centred copy and a neutral-fill category legend.",
  },
  {
    slideNumber: 7,
    anchor: "UBS-REF-STRIPTABLE",
    caption:
      "Strip-keyed financial table: narrow series-colour column, Pastel I current-column highlight through the heading, dashed assumption callouts.",
  },
  {
    slideNumber: 3,
    anchor: "UBS-REF-PROCESS",
    caption:
      "Small-multiple bars sharing one external category column, above a numbered three-step process band.",
  },
  {
    slideNumber: 1,
    anchor: "UBS-REF-CHARTBAND",
    caption:
      "Single-series column chart: y-axis removed, outside-end labels, Pastel I period band, manual average key beneath the plot.",
  },
];

export const BRANDS: BrandConfig[] = [
  {
    id: "citi",
    label: "Citi",
    toggleLabel: "Citi defaults",
    sectionTitle: "Citi Style",
    sectionDescription: "Apply Citi brand colors, fonts, and chart formatting.",
    accentColorHex: "#255BE3",
    companyId: "4418",
    themeColors: CITI_THEME_COLORS,
    slideDimensions: { ...CITI_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...CITI_CHART_SERIES_PALETTE],
    stylePrompt: CITI_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Citi_blank.pptx",
    referenceTemplateAsset: "assets/templates/Citi.pptx",
    snippetsEnabled: true,
    extraSnippetTypes: [...CITI_EXTRA_SNIPPET_TYPES],
    snippetStyleProfile: CITI_SNIPPET_STYLE_PROFILE,
  },
  {
    id: "bmo",
    label: "BMO",
    toggleLabel: "BMO defaults",
    sectionTitle: "BMO Style",
    sectionDescription: "Apply BMO brand colors, fonts, and chart formatting.",
    accentColorHex: "#0079C1",
    companyId: "2151",
    themeColors: BMO_THEME_COLORS,
    slideDimensions: { ...BMO_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...BMO_CHART_SERIES_PALETTE],
    stylePrompt: BMO_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/BMO_blank.pptx",
    referenceTemplateAsset: "assets/templates/BMO.pptx",
    snippetsEnabled: false,
    snippetStyleProfile: DEFAULT_SNIPPET_STYLE_PROFILE,
  },
  {
    id: "ubs",
    label: "UBS",
    toggleLabel: "UBS defaults",
    sectionTitle: "UBS Style",
    sectionDescription: "Apply UBS brand colors, fonts, and chart formatting.",
    accentColorHex: "#E60000",
    companyId: "6138",
    themeColors: UBS_THEME_COLORS,
    slideDimensions: { ...UBS_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...UBS_CHART_SERIES_PALETTE],
    stylePrompt: UBS_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/UBS_blank.pptx",
    referenceTemplateAsset: "assets/templates/UBS.pptx",
    snippetsEnabled: true,
    snippetStyleProfile: UBS_SNIPPET_STYLE_PROFILE,
    // UBS keeps its logo and message furniture on layouts (not the master), so
    // unqualified body-slide inserts use the verified branded content layout.
    contentLayoutName: "Blank Slide with Message",
    referenceMode: "curated-images",
    referenceSlides: UBS_REFERENCE_SLIDES,
  },
  {
    id: "safra",
    label: "Safra",
    toggleLabel: "Safra defaults",
    sectionTitle: "Safra Style",
    sectionDescription:
      "Apply Safra brand colors, fonts, and chart formatting.",
    accentColorHex: "#00003C", // Safra deep navy (colorScheme accent1)
    companyId: "481220",
    themeColors: SAFRA_THEME_COLORS,
    slideDimensions: { ...SAFRA_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...SAFRA_CHART_SERIES_PALETTE],
    stylePrompt: SAFRA_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Safra_blank.pptx",
    referenceTemplateAsset: "assets/templates/Safra.pptx",
    snippetsEnabled: false,
    // Logo / call-out furniture lives on the Call Out layouts (0 blips on the
    // slide master). Unqualified body-slide inserts use this branded content
    // layout so they inherit that furniture.
    contentLayoutName: "Call Out (texto)",
  },
  {
    id: "evercore",
    label: "Evercore",
    toggleLabel: "Evercore defaults",
    sectionTitle: "Evercore Style",
    sectionDescription:
      "Apply Evercore brand colors, fonts, and chart formatting.",
    accentColorHex: "#14879B", // Evercore teal (colorScheme accent1)
    companyId: "6116",
    themeColors: EVERCORE_THEME_COLORS,
    slideDimensions: { ...EVERCORE_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...EVERCORE_CHART_SERIES_PALETTE],
    stylePrompt: EVERCORE_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Evercore_blank.pptx",
    referenceTemplateAsset: "assets/templates/Evercore.pptx",
    snippetsEnabled: false,
    // Evercore's logo + brand furniture live on the slide master, so default
    // AddSlide inherits it — no contentLayoutName needed (same as Citi/BMO).
  },
  {
    id: "messier",
    label: "Messier",
    toggleLabel: "Messier defaults",
    sectionTitle: "Messier Style",
    sectionDescription:
      "Apply Messier brand colors, fonts, and chart formatting.",
    accentColorHex: "#002060", // Messier navy (colorScheme dark2)
    companyId: "1419554",
    themeColors: MESSIER_THEME_COLORS,
    slideDimensions: { ...MESSIER_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...MESSIER_CHART_SERIES_PALETTE],
    stylePrompt: MESSIER_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Messier_blank.pptx",
    referenceTemplateAsset: "assets/templates/Messier.pptx",
    snippetsEnabled: false,
    // Messier's logo + brand furniture live on the slide master(s), so default
    // AddSlide inherits it — no contentLayoutName needed (same as Citi/BMO).
  },
  {
    id: "cavendish",
    label: "Cavendish",
    toggleLabel: "Cavendish defaults",
    sectionTitle: "Cavendish Style",
    sectionDescription:
      "Apply Cavendish brand colors, fonts, and chart formatting.",
    accentColorHex: "#1D428A", // Cavendish blue (colorScheme accent1)
    companyId: "507879",
    themeColors: CAVENDISH_THEME_COLORS,
    slideDimensions: { ...CAVENDISH_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...CAVENDISH_CHART_SERIES_PALETTE],
    stylePrompt: CAVENDISH_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Cavendish_blank.pptx",
    referenceTemplateAsset: "assets/templates/Cavendish.pptx",
    snippetsEnabled: false,
    // Cavendish's logo + brand furniture live on the slide master, so default
    // AddSlide inherits it — no contentLayoutName needed (same as Citi/BMO).
  },
  {
    id: "maFinancial",
    label: "MA Financial",
    toggleLabel: "MA Financial defaults",
    sectionTitle: "MA Financial Style",
    sectionDescription:
      "Apply MA Financial brand colors, fonts, and chart formatting.",
    accentColorHex: "#2D3CB4", // MA Financial blue (colorScheme accent1)
    companyId: "945559",
    themeColors: MA_FINANCIAL_THEME_COLORS,
    slideDimensions: { ...MA_FINANCIAL_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...MA_FINANCIAL_CHART_SERIES_PALETTE],
    stylePrompt: MA_FINANCIAL_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/MAFinancial_blank.pptx",
    referenceTemplateAsset: "assets/templates/MAFinancial.pptx",
    snippetsEnabled: false,
    // MA Financial's logo + brand furniture live on the slide master, so
    // default AddSlide inherits it — no contentLayoutName needed (same as
    // Citi/BMO). blankTemplateAsset is a mechanically-generated 1-slide blank
    // (empty "Title and Content" placeholders only); worth a quick manual
    // sanity check in PowerPoint before this brand ships.
  },
  {
    id: "stj",
    label: "STJ Advisors",
    toggleLabel: "STJ defaults",
    sectionTitle: "STJ Style",
    sectionDescription:
      "Apply STJ Advisors brand colors, fonts, and chart formatting.",
    accentColorHex: "#022C6A", // STJ navy (colorScheme accent1)
    companyId: "553729",
    themeColors: STJ_THEME_COLORS,
    slideDimensions: { ...STJ_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...STJ_CHART_SERIES_PALETTE],
    stylePrompt: STJ_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/STJ_blank.pptx",
    referenceTemplateAsset: "assets/templates/STJ.pptx",
    snippetsEnabled: false,
    // Logo lives on the slide master; the master's first layout is already
    // "2_Title Slide", so default AddSlide inherits furniture — no
    // contentLayoutName needed (same as Citi/BMO). blankTemplateAsset is a
    // mechanically-generated 1-slide blank (empty "2_Title Slide" placeholders).
  },
  {
    id: "berenberg",
    label: "Berenberg",
    toggleLabel: "Berenberg defaults",
    sectionTitle: "Berenberg Style",
    sectionDescription:
      "Apply Berenberg brand colors, fonts, and chart formatting.",
    accentColorHex: "#FF5229", // Berenberg orange (colorScheme dk2)
    companyId: "225898",
    themeColors: BERENBERG_THEME_COLORS,
    slideDimensions: { ...BERENBERG_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...BERENBERG_CHART_SERIES_PALETTE],
    stylePrompt: BERENBERG_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Berenberg_blank.pptx",
    referenceTemplateAsset: "assets/templates/Berenberg.pptx",
    snippetsEnabled: false,
    // Vector wordmark lives on slide master 1, but that master's first layout
    // is Cover, so unqualified body-slide inserts use the branded content
    // layout. Catalog matching and AddSlide trim layout names, so this is the
    // trimmed form of the OOXML cSld name ("Title and content ").
    contentLayoutName: "Title and content",
  },
  {
    id: "flagstaff",
    label: "Flagstaff",
    toggleLabel: "Flagstaff defaults",
    sectionTitle: "Flagstaff Style",
    sectionDescription:
      "Apply Flagstaff brand colors, fonts, and chart formatting.",
    accentColorHex: "#507396", // Flagstaff blue (colorScheme accent1)
    companyId: "693796",
    themeColors: FLAGSTAFF_THEME_COLORS,
    slideDimensions: { ...FLAGSTAFF_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...FLAGSTAFF_CHART_SERIES_PALETTE],
    stylePrompt: FLAGSTAFF_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Flagstaff_blank.pptx",
    referenceTemplateAsset: "assets/templates/Flagstaff.pptx",
    snippetsEnabled: false,
    // The deck has layout-specific image furniture, so use its primary branded
    // body layout for unqualified content-slide inserts.
    contentLayoutName: "Heading and 1-Column Content NEW",
  },
  {
    id: "pareto",
    label: "Pareto",
    toggleLabel: "Pareto defaults",
    sectionTitle: "Pareto Style",
    sectionDescription:
      "Apply Pareto brand colors, fonts, and chart formatting.",
    accentColorHex: "#003255", // Pareto navy (colorScheme accent1 / dk2)
    companyId: "299479",
    themeColors: PARETO_THEME_COLORS,
    slideDimensions: { ...PARETO_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...PARETO_CHART_SERIES_PALETTE],
    stylePrompt: PARETO_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Pareto_blank.pptx",
    referenceTemplateAsset: "assets/templates/Pareto.pptx",
    snippetsEnabled: false,
    // Wordmark lives on slide master 1, but that master's first layout is
    // Blank, so unqualified body-slide inserts use the branded "Text" layout.
    // Catalog matching and AddSlide trim layout names.
    contentLayoutName: "Text",
  },
  {
    id: "mediobanca",
    label: "Mediobanca",
    toggleLabel: "Mediobanca defaults",
    sectionTitle: "Mediobanca Style",
    sectionDescription:
      "Apply Mediobanca brand colors, fonts, and chart formatting.",
    accentColorHex: "#003366", // Mediobanca navy (colorScheme accent1 / dk1)
    companyId: "462202",
    themeColors: MEDIOBANCA_THEME_COLORS,
    slideDimensions: { ...MEDIOBANCA_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...MEDIOBANCA_CHART_SERIES_PALETTE],
    stylePrompt: MEDIOBANCA_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Mediobanca_blank.pptx",
    referenceTemplateAsset: "assets/templates/Mediobanca.pptx",
    snippetsEnabled: false,
    // Wordmark lives on slide master 1, but that master's first layout is
    // Cover Title, so unqualified body-slide inserts use the branded
    // "Title and Content" layout.
    contentLayoutName: "Title and Content",
  },
  {
    id: "nordea",
    label: "Nordea",
    toggleLabel: "Nordea defaults",
    sectionTitle: "Nordea Style",
    sectionDescription:
      "Apply Nordea brand colors, fonts, and chart formatting.",
    accentColorHex: "#00005E", // Nordea navy (colorScheme accent1 / dk2)
    companyId: "818921",
    themeColors: NORDEA_THEME_COLORS,
    slideDimensions: { ...NORDEA_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...NORDEA_CHART_SERIES_PALETTE],
    stylePrompt: NORDEA_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/Nordea_blank.pptx",
    referenceTemplateAsset: "assets/templates/Nordea.pptx",
    snippetsEnabled: false,
    // Wordmark lives on slide master 1, but that master's first layout is
    // Title Slide and there is no "Title and Content". Unqualified body
    // inserts must use "Two Content" — the generic name picker would
    // otherwise choose "4 up callout". "Create Layout 1" is specialized.
    contentLayoutName: "Two Content",
  },
  {
    id: "default",
    label: "Finster Demo",
    toggleLabel: "Finster Demo defaults",
    sectionTitle: "Finster Demo Style",
    sectionDescription:
      "Apply Finster Demo brand colors, fonts, and chart formatting.",
    accentColorHex: "#042355", // deep navy (colorScheme accent1)
    // Flag-only demo brand (pptxTemplateBrand=default). Empty companyId
    // so it never auto-matches via FactSet CompanyId.
    companyId: "",
    themeColors: FINSTER_DEMO_THEME_COLORS,
    slideDimensions: { ...FINSTER_DEMO_SLIDE_DIMENSIONS },
    chartSeriesPalette: [...FINSTER_DEMO_CHART_SERIES_PALETTE],
    stylePrompt: FINSTER_DEMO_STYLE_PROMPT,
    blankTemplateAsset: "assets/templates/default_blank.pptx",
    referenceTemplateAsset: "assets/templates/default.pptx",
    snippetsEnabled: false,
    // Furniture lives on the slide master, so default AddSlide inherits it —
    // no contentLayoutName needed. blankTemplateAsset is a mechanically-
    // generated 1-slide blank (DEFAULT layout).
  },
];

const BRANDS_BY_ID = new Map<BrandId, BrandConfig>(BRANDS.map((b) => [b.id, b]));

/** Resolve a brand config by id. Returns `undefined` for null/unknown ids. */
export function getBrand(id: BrandId | null | undefined): BrandConfig | undefined {
  if (!id) return undefined;
  return BRANDS_BY_ID.get(id);
}

/** Style prompt for a brand id, or "" when no brand is active. */
export function getBrandStylePrompt(id: BrandId | null | undefined): string {
  return getBrand(id)?.stylePrompt ?? "";
}

/** Type guard for raw strings flowing in from URLs / serialized payloads. */
export function isBrandId(value: unknown): value is BrandId {
  return typeof value === "string" && BRANDS_BY_ID.has(value as BrandId);
}
